import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { ArrowLeft, Users, Calendar, Plus, Eye, Scale, MessageSquare, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Calibracao180 = () => {
  const navigate = useNavigate();
  const { cicloId } = useParams();
  
  const [loading, setLoading] = useState(false);
  const [sessoes, setSessoes] = useState([]);
  const [ciclo, setCiclo] = useState(null);
  const [showNewSession, setShowNewSession] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [comparativo, setComparativo] = useState(null);
  
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    data_sessao: '',
    gestores_participantes: [],
    facilitador_id: ''
  });

  useEffect(() => {
    fetchDados();
  }, [cicloId]);

  const fetchDados = async () => {
    try {
      const [sessoesRes, cicloRes] = await Promise.all([
        axios.get(`${API}/api/feedback-180/calibracao/ciclo/${cicloId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/api/feedback-180/ciclos/${cicloId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      
      setSessoes(sessoesRes.data);
      setCiclo(cicloRes.data);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      toast.error('Erro ao carregar dados');
    }
  };

  const handleCreateSession = async () => {
    try {
      setLoading(true);
      
      await axios.post(`${API}/api/feedback-180/calibracao`, {
        ...formData,
        ciclo_id: cicloId
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Sessão criada com sucesso!');
      setShowNewSession(false);
      setFormData({
        nome: '',
        descricao: '',
        data_sessao: '',
        gestores_participantes: [],
        facilitador_id: ''
      });
      fetchDados();
    } catch (error) {
      toast.error('Erro ao criar sessão');
    } finally {
      setLoading(false);
    }
  };

  const handleViewComparative = async (sessaoId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/api/feedback-180/calibracao/${sessaoId}/comparativo`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      setComparativo(response.data);
      setSelectedSession(sessaoId);
    } catch (error) {
      toast.error('Erro ao carregar comparativo');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      agendada: 'bg-blue-100 text-blue-700',
      em_andamento: 'bg-yellow-100 text-yellow-700',
      concluida: 'bg-green-100 text-green-700'
    };
    
    return (
      <Badge className={colors[status] || colors.agendada}>
        {status === 'agendada' ? 'Agendada' :
         status === 'em_andamento' ? 'Em Andamento' :
         'Concluída'}
      </Badge>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Button variant="outline" onClick={() => navigate(`/feedback/180/dashboard/${cicloId}`)} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Voltar
      </Button>

      <div className="space-y-6">
        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl flex items-center space-x-2">
                  <Scale className="h-6 w-6 text-purple-600" />
                  <span>Sistema de Calibração</span>
                </CardTitle>
                <CardDescription>
                  {ciclo?.nome || 'Carregando...'} - Calibre avaliações entre gestores
                </CardDescription>
              </div>
              
              <Dialog open={showNewSession} onOpenChange={setShowNewSession}>
                <DialogTrigger asChild>
                  <Button className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Nova Sessão
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Criar Sessão de Calibração</DialogTitle>
                    <DialogDescription>
                      Agende uma reunião para calibrar avaliações entre gestores
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nome da Sessão</Label>
                      <Input
                        value={formData.nome}
                        onChange={(e) => setFormData({...formData, nome: e.target.value})}
                        placeholder="Ex: Calibração Q2 2025 - Área Comercial"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Descrição</Label>
                      <Textarea
                        value={formData.descricao}
                        onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                        placeholder="Objetivo da sessão..."
                        rows={3}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Data e Hora</Label>
                      <Input
                        type="datetime-local"
                        value={formData.data_sessao}
                        onChange={(e) => setFormData({...formData, data_sessao: e.target.value})}
                      />
                    </div>
                    
                    <div className="flex justify-end space-x-2">
                      <Button variant="outline" onClick={() => setShowNewSession(false)}>
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleCreateSession}
                        disabled={loading || !formData.nome || !formData.data_sessao}
                        className="bg-purple-600"
                      >
                        {loading ? 'Criando...' : 'Criar Sessão'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
        </Card>

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <Scale className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900 mb-1">O que é Calibração?</h4>
                <p className="text-sm text-blue-700">
                  A calibração garante equidade nas avaliações comparando notas entre gestores. 
                  Em sessões de calibração, gestores discutem e ajustam avaliações para manter consistência 
                  nos critérios e evitar vieses individuais.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lista de Sessões */}
        <Card>
          <CardHeader>
            <CardTitle>Sessões de Calibração</CardTitle>
          </CardHeader>
          <CardContent>
            {sessoes.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">Nenhuma sessão criada ainda</p>
                <Button onClick={() => setShowNewSession(true)} className="bg-purple-600">
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Primeira Sessão
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {sessoes.map((sessao) => (
                  <div key={sessao.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold">{sessao.nome}</h3>
                          {getStatusBadge(sessao.status)}
                        </div>
                        
                        {sessao.descricao && (
                          <p className="text-sm text-gray-600 mb-3">{sessao.descricao}</p>
                        )}
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-4 w-4" />
                            <span>{new Date(sessao.data_sessao).toLocaleString()}</span>
                          </div>
                          
                          <div className="flex items-center space-x-1">
                            <Users className="h-4 w-4" />
                            <span>{sessao.gestores_participantes?.length || 0} gestores</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewComparative(sessao.id)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Ver Comparativo
                        </Button>
                        
                        {sessao.status === 'agendada' && (
                          <Button size="sm" className="bg-purple-600">
                            Iniciar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Comparativo Modal */}
        {comparativo && (
          <Card>
            <CardHeader>
              <CardTitle>Comparativo de Avaliações</CardTitle>
              <CardDescription>
                Compare notas entre diferentes gestores para o mesmo colaborador
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.keys(comparativo.comparativo || {}).length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    Nenhum dado de comparação disponível ainda
                  </p>
                ) : (
                  <div className="text-sm text-gray-600">
                    <p>Dados de comparação carregados para {Object.keys(comparativo.comparativo).length} colaborador(es)</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Interface de comparação detalhada em desenvolvimento
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Guia de Calibração */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Guia de Calibração</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <h4 className="font-semibold">Prepare</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Crie sessão, convide gestores e defina colaboradores a serem calibrados
                </p>
              </div>
              
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <h4 className="font-semibold">Compare</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Analise diferenças nas avaliações e discuta critérios utilizados
                </p>
              </div>
              
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <h4 className="font-semibold">Ajuste</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Registre ajustes e justificativas para garantir equidade
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Calibracao180;
